// The salon's templates, as Bailey should know them: every email in the
// template library, the text starters and the kinds of post Social makes, each
// with the words owners actually use for it ("bring a mate", "prices going
// up", "dead tuesdays"). A message's words pick the few that fit, so Bailey
// starts from the salon's own template instead of a blank page.
//
// Messages about their bookings are spotted too, so a reminder ask is pointed
// at the automated messages rather than turned into a campaign.
import { TEMPLATES } from '../../../marketing-v2/features/email-composer/data/templates'
import { marketFor } from '../../../marketing-v2/features/email-composer/email/theme'
import { SMS_IDEAS } from '../../../marketing-v2/features/sms-composer/smsIdeas'

// ─── Words ───
// What people mean by a few words together, said as one word: "haven't been
// in for ages" is lapsed, "we now do beard dyes" is something new, "no shows"
// is a no-show. Run before the words are split.
const PHRASES = [
  [/\b(haven'?t|havent|hasn'?t|hasnt|not|aint|never)\s+(been|booked|rebooked|come|came|visited|seen)\b/g, ' lapsed '],
  [/\b(been|for|in)\s+(a while|ages|a bit|months)\b/g, ' lapsed '],
  [/\bsince (the |before |last |their last )?(summer|spring|autumn|winter|christmas|xmas|january|colour|visit)\b/g, ' lapsed '],
  [/\bmiss(ed)? (you|them|ya|u)\b/g, ' winback '],
  [/\b(now (do|doing|stock|stocking|sell|selling|offer|offering|take|taking)|(are|is) back|just (added|launched|started)|launch(ed|ing)?|now available)\b/g, ' launch '],
  [/\b(open|opening|close|closing|shut)\s+(late|early|sundays?|mondays?|till|until)\b|\bopening (hours|times)\b/g, ' hours '],
  [/\b(moved|moving|relocat\w*|new address|new premises|bigger room)\b/g, ' moved '],
  [/\b(card only|cashless|no cash|online booking|booking link|new website|new number)\b/g, ' announcement '],
  [/\bno[\s-]?shows?\b/g, ' noshow '],
  [/\blast[\s-]minute\b/g, ' lastminute '],
  [/\b(black|good) friday\b/g, ' sale '],
  [/\b\d+\s?%/g, ' percent off '],
  [/£\s?\d+|\b\d+\s?(quid|pound|pounds|p)\b/g, ' money '],
  [/\b(going|gone|went) (up|from)\b|\bputting (prices |them )?up\b|\bprice (rise|increase|change)s?\b|\bnew prices?\b/g, ' increase '],
  [/\bbring (a|your) (mate|friend|pal)\b|\bword of mouth\b/g, ' refer '],
  [/\bmat(ernity)? leave\b|\btaking over\b/g, ' returning '],
  [/\bfathers?'?s? day\b/g, ' father '],
  [/\bmothers?'?s? day\b/g, ' mother '],
]
// The same word however it's typed: "texts", "texting" and "txt" are a text;
// "insta" and "ig" are Instagram
const SAME = {
  txt: 'text', sms: 'text', texting: 'text', texted: 'text',
  mail: 'email', emails: 'email', emailing: 'email', mailer: 'email', mailout: 'email', newsletters: 'newsletter',
  insta: 'instagram', ig: 'instagram', gram: 'instagram', fb: 'facebook', socials: 'social', reels: 'reel', stories: 'story',
  appt: 'appointment', appts: 'appointment', appointments: 'appointment', bookings: 'booking', booked: 'booking',
  vouchers: 'voucher', giftcard: 'voucher', giftcards: 'voucher', xmas: 'christmas', crimbo: 'christmas',
  bday: 'birthday', birthdays: 'birthday', mates: 'mate', pal: 'mate', pals: 'mate', friend: 'mate', friends: 'mate',
  referral: 'refer', referrals: 'refer', referring: 'refer', recommend: 'refer',
  discount: 'off', discounts: 'off', deal: 'offer', deals: 'offer', promo: 'offer', promotion: 'offer',
  customers: 'client', customer: 'client', clients: 'client',
  tues: 'tuesday', tue: 'tuesday', weds: 'wednesday', wed: 'wednesday', thurs: 'thursday', thur: 'thursday', thu: 'thursday',
  mon: 'monday', fri: 'friday', sat: 'saturday', sun: 'sunday', hol: 'holiday', hols: 'holiday',
  gaps: 'gap', spaces: 'space', slots: 'slot', openings: 'opening', cancellations: 'cancellation', cancelled: 'cancellation', cancel: 'cancellation',
}
const SKIP = new Set('the and for with that this what how can you your our are was have has get got make made want need into from about just like when where which who why will would could should there their them they then than some any all its his her him she he me my mine to of in on at by an as is it do does did be been being or if so up out not no yes please thanks hi hello hey pls plz cheers'.split(' '))
const stem = (w) => {
  if (SAME[w]) return SAME[w]
  if (w.length > 5 && /ing$/.test(w)) return w.slice(0, -3)
  if (w.length > 4 && /s$/.test(w) && !/ss$/.test(w)) return w.slice(0, -1)
  return w
}
export const wordsOf = (text) => {
  let t = String(text || '').toLowerCase().replace(/’/g, "'")
  PHRASES.forEach(([re, to]) => { t = t.replace(re, to) })
  return t.replace(/'s\b/g, '').replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 1 && !SKIP.has(w))
    .map(stem)
}
const has = (said, list) => list.some(w => said.includes(w))
// Words too common to pick a template on their own: they count half
const WEAK = new Set(wordsOf('week today tomorrow back email text post client everyone people free regular regulars list'))

// ─── What's in the library ───
// The words each email goes by, beyond its name and description
const DAYS = 'monday tuesday wednesday thursday friday saturday sunday'
const EMAIL_WORDS = {
  welcome: 'welcome new client first visit time hello joined found',
  offers: `quiet dead empty midweek slow fill chair slot gap space availability offer off percent money student january ${DAYS}`,
  rebook: 'rebook back lapsed winback miss regular overdue trim grown nudge win due sooner',
  news: 'news update announce announcement newsletter monthly',
  seasonal: 'voucher gift christmas present festive',
  loyalty: 'loyal loyalty thank reward',
  plain: 'note plain simple letter personal',
}
const EMAIL_EXTRA = {
  'hair-colour-season': 'colour color balayage highlight blonde autumn spring summer winter season shade',
  'hair-newsletter': 'newsletter monthly edit roundup october november halloween',
  'hair-new-stylist': 'new stylist joined joining starting team introduce meet hire returning',
  'hair-atelier': 'campaign general showcase signature prom wedding bridal updo',
  'barber-welcome': 'shop how works',
  'barber-rebook': 'fade grown trim lineup line shape skin weeks',
  'barber-midweek': 'midweek',
  'barber-new-barber': 'new barber joined joining starting team introduce meet hire returning apprentice chair',
  'barber-gift': 'father dad christmas',
  'barber-refer': 'refer mate bring recruit',
  'barber-poster': 'poster bold loud campaign wedding groom movember',
  'beauty-new-treatment': 'new treatment launch facial brow lash nail service wedding bridal makeup',
  'beauty-nails-offer': 'nail nails lash lashes glow',
  'beauty-infill': 'infill lash nail refill lapsed',
  'beauty-gift': 'spa mother mum valentine',
  'beauty-newsletter': 'skin skincare routine tips',
  'all-neighbourhood': 'neighbourhood local magazine newsletter',
  'all-birthday': 'birthday',
  'all-thank-you': 'thank review google stars feedback after visit voted award',
  'all-holiday-hours': 'christmas holiday bank easter closed closing shut closure summer',
  'all-price-update': 'price increase money change',
  'plain-note': 'note personal letter sorry apology retiring retire goodbye',
  'plain-announcement': 'announcement announce news moved hours change closed closure launch online',
}
const SMS_WORDS = {
  'sms-fill': `quiet dead empty fill opening week slow chair gap space availability slot evening party half term ${DAYS}`,
  'sms-winback': 'back lapsed winback miss while overdue win',
  'sms-last-minute': 'lastminute cancellation noshow opened today one',
  'sms-flash-offer': 'offer off flash percent money sale only',
  'sms-new': 'new launch added try now',
}
// The kinds of post Social makes (the post block's "kind")
const POSTS = [
  { kind: 'freeSlots', name: 'Free slots', about: 'The free times from the diary for a day or a few days.', words: `free slot gap space availability available opening quiet dead empty fill tomorrow today week weekend ${DAYS}` },
  { kind: 'oneSlot', name: 'One slot', about: 'A single opening, often a late cancellation.', words: 'one slot cancellation lastminute noshow left spare' },
  { kind: 'freshWork', name: 'Fresh work', about: 'Show off a cut, colour or set just done.', words: 'work cut fade taper crop bob lob mullet copper balayage blonde colour nails set gel lashes brow look photo pic picture done did show' },
  { kind: 'beforeAfter', name: 'Before and after', about: 'Two photos, before and after.', words: 'before after transformation' },
  { kind: 'review', name: 'Review', about: 'A client review, word for word.', words: 'review star google feedback testimonial screenshot share' },
  { kind: 'team', name: 'Team', about: 'Introduce someone on the team.', words: 'team new barber stylist tech therapist joined joining meet introduce staff apprentice qualified training returning' },
  { kind: 'newService', name: 'New service', about: 'A new service or treatment.', words: 'new service treatment launch added menu' },
  { kind: 'priceList', name: 'Price list', about: 'The menu with prices.', words: 'price list menu cost' },
  { kind: 'tips', name: 'Tips', about: 'An aftercare or styling tip.', words: 'tip aftercare advice care product keep looking after between home fresh' },
  { kind: 'question', name: 'Question', about: 'A question to get people talking.', words: 'question poll ask engagement vote which choose' },
  { kind: 'offer', name: 'Offer', about: 'A deal or money off.', words: 'offer off percent money sale promo halloween' },
  { kind: 'giftVouchers', name: 'Gift vouchers', about: 'Sell gift vouchers.', words: 'voucher gift christmas father mother present' },
]

// Which channel they asked for, if they said
const CHANNEL_WORDS = {
  email: wordsOf('email newsletter inbox'),
  sms: wordsOf('text'),
  post: wordsOf('post instagram facebook tiktok story reel grid social caption'),
}
export const channelsSaid = (text) => {
  const said = wordsOf(text)
  return Object.keys(CHANNEL_WORDS).filter(c => has(said, CHANNEL_WORDS[c]))
}

// The library as one list: { channel, id, name, about, market, title, named,
// told }: the words of its name, the words it goes by, and its description
const LIBRARY = [
  ...TEMPLATES.map(t => ({
    channel: 'email', id: t.id, name: t.name, about: t.description || '', market: t.market,
    title: wordsOf(t.name), named: wordsOf(`${EMAIL_WORDS[t.purpose] || ''} ${EMAIL_EXTRA[t.id] || ''}`), told: wordsOf(t.description),
  })),
  ...SMS_IDEAS.map(s => ({
    channel: 'sms', id: s.id, name: s.label, about: s.text, market: 'all',
    title: wordsOf(s.label), named: wordsOf(SMS_WORDS[s.id] || ''), told: wordsOf(s.text.replace(/\{\{\w+\}\}/g, '')),
  })),
  ...POSTS.map(p => ({
    channel: 'post', id: p.kind, name: p.name, about: p.about, market: 'all',
    title: wordsOf(p.name), named: wordsOf(p.words), told: wordsOf(p.about),
  })),
]

// The salon's trade: hair, barber or beauty (hair when it isn't set)
export const marketOf = (user) => {
  const salon = (user && user.hairdresser && user.hairdresser.salon) || {}
  return marketFor(salon.businessType || (user && user.businessType)) || 'hair'
}

// ─── Asks no marketing template is for ───
// A rota, a form, a policy, an invoice, a job ad, a printout, a reply to a
// review: Bailey helps with those directly rather than reaching for an email
const NOT_MARKETING = /\b(rota|invoice|receipt|polic(y|ies)|consent|consultation form|forms?\b.*\bfor\b|job ad|job advert|advert for a|hiring|vacanc(y|ies)|indeed|leaflet|a4|printable|print out|contract|t ?& ?cs|terms and conditions)\b|\b(reply|respond|answer)\b.*\breview\b/i
// Appointment reminders are the salon's automated messages, not a campaign
const REMINDER = /\bremind(er|ers|ing)?\b/i
const isReminder = text => REMINDER.test(String(text || '')) && aboutBookings(text)
// A question about the diary ("who's in tomorrow?") rather than an ask to make something
const DIARY_QUESTION = /^\s*(who'?s|whos|who is|who has|how many|how busy|what'?s on|whats on|am i|are we)\b/i
// Doing something in the diary ("book Joe in with Dan for a skin fade"): the
// service names match templates, and a list of them made Bailey second-guess
// an email it had drafted earlier in the chat (stag 10-08)
const DIARY_ACTION = /^\s*(please\s+)?((can|could) you\s+)?(book|re-?book|reschedule|move|cancel|squeeze|fit)\b/i

// ─── The templates a message is about ───
// Their own trade's templates and the ones for every business; the channel
// they named, if they named one. The words of its name count three, words it
// goes by two and its description one; common words count half.
export const relevantTemplates = (text, { market = 'hair', max = 4, channel = null } = {}) => {
  const raw = String(text || '')
  if (NOT_MARKETING.test(raw) || DIARY_QUESTION.test(raw) || DIARY_ACTION.test(raw) || isReminder(raw)) return []
  const want = Array.from(new Set(wordsOf(raw)))
  if (!want.length) return []
  const channels = channel ? [channel] : channelsSaid(raw)
  return LIBRARY
    .filter(t => t.market === 'all' || t.market === market)
    .filter(t => !channels.length || channels.includes(t.channel))
    .map((t) => {
      const score = want.reduce((n, w) => {
        const weight = WEAK.has(w) ? 0.5 : 1
        return n + weight * (t.title.includes(w) ? 3 : t.named.includes(w) ? 2 : t.told.includes(w) ? 1 : 0)
      }, 0)
      // Their own trade's version wins a tie with the every-business one
      return { t, score: score + (score && t.market === market ? 0.5 : 0) }
    })
    .filter(x => x.score >= 2)
    .sort((a, b) => b.score - a.score)
    .slice(0, max)
    .map(x => x.t)
}

// ─── Whether it's about their bookings ───
// A booking word on its own, or a time ("tues", "this week") with a quiet one
// ("dead", "empty", "fill")
const BOOKING_WORDS = wordsOf('booking appointment diary slot gap space opening availability available cancellation noshow lastminute')
const TIME_WORDS = wordsOf(`today tomorrow tonight week weekend morning afternoon evening ${DAYS}`)
const QUIET_WORDS = wordsOf('quiet dead empty slow fill busy')
export const aboutBookings = (text) => {
  const said = wordsOf(text)
  return has(said, BOOKING_WORDS) || (has(said, TIME_WORDS) && has(said, QUIET_WORDS))
}

// ─── For the context ───
const line = (t) => {
  if (t.channel === 'email') return `- email "${t.name}" (template "${t.id}"): ${t.about}`
  if (t.channel === 'sms') return `- text "${t.name}" (${t.id}), to start from: ${t.about.replace(/\n/g, ' / ')}`
  return `- post "${t.name}" (kind "${t.id}"): ${t.about}`
}
const ASKED_FOR_TEMPLATES = /\btemplates?\b|\bexamples?\b|\bideas?\b/i

// The lines for one message: the booking look-up first, then the templates
// that fit (or, when they asked for templates and none fit, what there is)
export const templateLines = (text, { market = 'hair' } = {}) => {
  // No "read the diary first" line for booking asks: across two full runs
  // (268 asks each, 2026-10-02) Bailey read the diary 92% of the time with it
  // and 94% without, and quoted no time it hadn't read either way. The server
  // prompt covers it; aboutBookings still spots reminders below.
  const lines = []
  if (isReminder(text)) {
    lines.push('Appointment reminders are the salon’s automated messages, not a marketing campaign: check what clients already get with comms_state and say where it’s set. Only write a one-off message if they still want one.')
    return lines
  }
  if (NOT_MARKETING.test(String(text || ''))) {
    lines.push('This isn’t one of their marketing templates: help with it directly, and if Beu has a place for it, say where.')
    return lines
  }
  const fits = relevantTemplates(text, { market })
  const emails = fits.filter(t => t.channel === 'email').map(t => `"${t.id}"`)
  if (fits.length) {
    lines.push('Their templates that fit this, best first (use the one that fits what they said: an email block sets "template" to its id exactly, a post sets "kind", a text starts from its words and keeps the opt-out line):', ...fits.map(line))
    if (emails.length) lines.push(`For a new email in this reply, "template" is one of ${emails.join(', ')}: an id, never a purpose or a kind of post.`)
    // Rebuilt from the latest message only: a draft from earlier in the chat
    // used one of theirs that fitted then, and isn't in this list.
    lines.push('This list is for their latest message only. Anything drafted earlier in the chat already uses one of their templates: leave it alone and don\'t mention it unless they ask.')
  } else if (ASKED_FOR_TEMPLATES.test(String(text || ''))) {
    const mine = LIBRARY.filter(t => t.market === 'all' || t.market === market)
    const named = c => mine.filter(t => t.channel === c).map(t => t.name).join(', ')
    lines.push(`They asked about templates. Theirs: emails (${named('email')}); texts (${named('sms')}); posts (${named('post')}). Ask which they want with a choices block, or offer the two that suit what they said.`)
  }
  return lines
}

// An email template Bailey named, if it's one of theirs. The model sometimes
// puts a purpose or a post kind there instead ("fill", "giftVouchers"), so
// those find the salon's own template for the same job.
const LOOSE = {
  fill: 'offers', offer: 'offers', offers: 'offers', freeSlots: 'offers', oneSlot: 'offers', quiet: 'offers',
  moment: 'seasonal', seasonal: 'seasonal', giftVouchers: 'seasonal', gift: 'seasonal', voucher: 'seasonal',
  winback: 'rebook', rebook: 'rebook',
  intro: 'news', team: 'news', news: 'news', newService: 'news', newsletter: 'news',
  welcome: 'welcome', loyalty: 'loyalty', review: 'loyalty', thanks: 'loyalty', plain: 'plain', note: 'plain',
}
// The one to pick for a job: the trade's own first (its plainest, not a
// featured showpiece), then the every-business one
const pickFor = (purpose, market) => {
  const of = m => TEMPLATES.filter(t => t.purpose === purpose && t.market === m)
  const own = of(market)
  return (own.find(t => !t.featured) || own[0] || of('all')[0] || {}).id
}
export const templateIdOf = (id, market = 'hair') => {
  if (!id) return undefined
  if (TEMPLATES.some(t => t.id === id)) return id
  if (id === 'intro') return { hair: 'hair-new-stylist', barber: 'barber-new-barber', beauty: 'beauty-new-treatment' }[market]
  if (id === 'review' || id === 'thanks') return 'all-thank-you'
  return LOOSE[id] ? pickFor(LOOSE[id], market) : undefined
}

// The template an email Bailey showed wears: the one it named, if that's one
// of theirs; else the one that best fits what they asked (the model doesn't
// always copy the id it was given); else what its loose name or purpose means
export const emailTemplateFor = (block, { market = 'hair', asked = '' } = {}) => {
  const b = block || {}
  if (TEMPLATES.some(t => t.id === b.template)) return b.template
  const fit = asked ? relevantTemplates(asked, { market, channel: 'email', max: 1 })[0] : null
  return (fit && fit.id) || templateIdOf(b.template, market) || undefined
}
